import type { MetaFunction } from "@remix-run/cloudflare";
import { useEffect, useRef, useState } from "react";
import { Link } from "@remix-run/react";
import Footer from "~/components/Footer";
import { useScrollReveal } from "~/lib/use-scroll-reveal";
import { SplashScreen } from "~/components/SplashScreen";
import styles from "~/styles/home.module.css";

export const meta: MetaFunction = () => [
  { title: "Studio — Prodigium Dynamics" },
  { name: "description", content: "Bringing visions to life. AI systems, digital products, and personas engineered with precision." },
];

const TICKER1 = [
  "Prodigium Dynamics", "✦", "The Science of Vision", "✦",
  "AI-Driven Products", "✦", "Est. 2026", "✦",
  "Global by Design", "✦", "Precision over Noise", "✦",
  "Depth over Reach", "✦", "Evidence over Assumption", "✦",
];

const TICKER2 = [
  "PageBurn", "✦", "Skills", "✦", "Brubri", "✦", "Naomi", "✦", "░░░░░░░░░", "✦",
  "Reading Technology", "✦", "System Prompts", "✦", "Cognitive OS", "✦",
  "Connection Layer", "✦", "Digital Publishing", "✦",
];

const MANIFESTO = [
  "We build what others can only describe.",
  "We don't chase trends — we engineer the conditions for them to exist.",
  "We don't ship features. We release convictions.",
  "Precision over noise. Depth over reach. Evidence over assumption.",
];

const PROJECTS = [
  { id: "pageburn", tag: "Reading Technology", name: "PAGEBURN", desc: "Reading shouldn't feel like work. PageBurn makes any text — no matter how dense — feel natural, effortless, and clear. Built for people who want to actually absorb what they read.", image: "pageburn.site — screenshot", href: "/pageburn", external: "https://pageburn.site", status: "live" as const },
  { id: "skills", tag: "AI System Prompts", name: "SKILLARQ", desc: "Engineered system prompts packaged as .MD files. Load into any AI and get consistent, structured output — every session, every model. Built from real production use.", image: "Skillarq — system prompts catalog", href: "/skillarq", external: null, status: "live" as const },
  { id: "brubri", tag: "Cognitive OS", name: "BRUBRI", desc: "A task execution system built for neurodivergent minds. Clear decision trees, minimal context switching, and momentum-based progress tracking. Your brain, structured.", image: "Brubri — cognitive OS", href: "/brubri", external: null, status: "beta" as const },
  { id: "naomi", tag: "Connection Layer", name: "NAOMI", desc: "Some things can't be automated. Feeling understood is one of them. Closer than an app. Further than you'd expect. Not built to assist. Built to remain.", image: "Naomi — visual", href: null, external: null, status: "soon" as const },
];

export default function StudioPage() {
  const heroRef = useRef<HTMLElement>(null);
  const heroCanvasRef = useRef<HTMLCanvasElement>(null);
  const globeCanvasRef = useRef<HTMLCanvasElement>(null);
  const [activeManifesto, setActiveManifesto] = useState(0);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);

  // Hero cloth-field animation
  useEffect(() => {
    const canvas = heroCanvasRef.current;
    const hero = heroRef.current;
    if (!canvas || !hero) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animId: number;
    let W = 0, H = 0;
    const SPACING = 22, DOT_R = 1.4;
    const mouse = { x: -999, y: -999 };

    type Dot = { ox: number; oy: number; colPhase: number; rowPhase: number; ampX: number; ampY: number; speed: number };
    let dots: Dot[] = [];

    const cv = canvas as HTMLCanvasElement;
    const hv = hero as HTMLElement;
    const cx2d = ctx as CanvasRenderingContext2D;

    const buildDots = () => {
      dots = [];
      const cols = Math.ceil(W / SPACING) + 1;
      const rows = Math.ceil(H / SPACING) + 1;
      for (let r = 0; r < rows; r++) {
        for (let col = 0; col < cols; col++) {
          dots.push({ ox: col * SPACING, oy: r * SPACING, colPhase: col * 0.18, rowPhase: r * 0.10, ampX: 8 + Math.sin(col * 0.7 + r * 0.3) * 5, ampY: 26 + Math.cos(col * 0.4 + r * 0.5) * 14, speed: 0.7 + (col % 5) * 0.08 });
        }
      }
    };

    const resize = () => { W = cv.width = hv.offsetWidth; H = cv.height = hv.offsetHeight; buildDots(); };

    let t = 0;
    const draw = () => {
      cx2d.clearRect(0, 0, W, H);
      t += 0.018;
      for (const d of dots) {
        const waveY = Math.sin(t * d.speed + d.colPhase) * d.ampY;
        const waveX = Math.cos(t * d.speed * 0.7 + d.rowPhase) * d.ampX;
        const x = d.ox + waveX, y = d.oy + waveY;
        const dx = x - mouse.x, dy = y - mouse.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const prox = Math.max(0, 1 - dist / 140);
        const norm = (Math.sin(t * d.speed + d.colPhase) + 1) / 2;
        const alpha = Math.min(0.10 + norm * 0.38 + prox * 0.55, 0.9);
        cx2d.beginPath();
        cx2d.arc(x, y, DOT_R + prox * 2.5, 0, Math.PI * 2);
        cx2d.fillStyle = `rgba(42,62,244,${alpha})`;
        cx2d.fill();
      }
      animId = requestAnimationFrame(draw);
    };

    const onMove = (e: MouseEvent) => { const rect = cv.getBoundingClientRect(); mouse.x = e.clientX - rect.left; mouse.y = e.clientY - rect.top; };
    const onLeave = () => { mouse.x = -999; mouse.y = -999; };
    const onResize = () => resize();

    hv.addEventListener("mousemove", onMove);
    hv.addEventListener("mouseleave", onLeave);
    window.addEventListener("resize", onResize);
    resize();
    draw();

    return () => { cancelAnimationFrame(animId); hv.removeEventListener("mousemove", onMove); hv.removeEventListener("mouseleave", onLeave); window.removeEventListener("resize", onResize); };
  }, []);

  // Globe animation
  useEffect(() => {
    const canvas = globeCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const gc = canvas as HTMLCanvasElement;
    const gctx = ctx as CanvasRenderingContext2D;
    const W = gc.width, H = gc.height;
    const cx = W / 2, cy = H / 2, R = W * 0.42;
    const DOTS = 600;
    let rot = 0, animId: number;
    const golden = Math.PI * (3 - Math.sqrt(5));
    const pts = Array.from({ length: DOTS }, (_, i) => { const y = 1 - (i / (DOTS - 1)) * 2; const r = Math.sqrt(1 - y * y); const theta = golden * i; return { x: Math.cos(theta) * r, y, z: Math.sin(theta) * r }; });

    const draw = () => {
      gctx.clearRect(0, 0, W, H);
      rot += 0.003;
      const tilt = 0.3;
      const projected = pts.map((d) => { const x = d.x * Math.cos(rot) + d.z * Math.sin(rot); const z = -d.x * Math.sin(rot) + d.z * Math.cos(rot); const yt = d.y * Math.cos(tilt) - z * Math.sin(tilt); const zt = d.y * Math.sin(tilt) + z * Math.cos(tilt); const scale = (zt + 2) / 3; return { px: cx + x * R * scale, py: cy + yt * R * scale, alpha: 0.08 + ((zt + 1) / 2) * 0.55, radius: 1.2 + ((zt + 1) / 2) * 1.2, depth: zt }; });
      projected.sort((a, b) => a.depth - b.depth);
      for (const p of projected) { gctx.beginPath(); gctx.arc(p.px, p.py, p.radius, 0, Math.PI * 2); gctx.fillStyle = `rgba(42,62,244,${Math.min(p.alpha, 0.85)})`; gctx.fill(); }
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(animId);
  }, []);

  // Manifesto cycling
  useEffect(() => {
    const id = setInterval(() => setActiveManifesto((i) => (i + 1) % MANIFESTO.length), 2200);
    return () => clearInterval(id);
  }, []);

  useScrollReveal();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSent(true);
    setTimeout(() => { setSent(false); setName(""); setEmail(""); setMessage(""); }, 3000);
  };

  return (
    <>
      <SplashScreen name="PRODIGIUM" tag="AI-Driven Studio" />
      <section className={styles.hero} ref={heroRef}>
        <canvas className={styles.heroCanvas} ref={heroCanvasRef} />
        <div className={styles.heroGlow} />
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>BRINGING<br />VISIONS</h1>
          <h2 className={styles.heroSubtitle}>TO LIFE.</h2>
          <div className={styles.heroCta}>
            <a href="#about" className="btn-primary">Who we are</a>
            <a href="#projects" className="btn-ghost">Our Work</a>
          </div>
        </div>
      </section>

      <div className={styles.tickerWrap}>
        <div className={styles.tickerTrack}>
          {[...TICKER1, ...TICKER1].map((t, i) => (
            <span key={i} className={styles.tickerItem}>
              {t === "✦" ? <span className={styles.tickerAccent}>✦</span> : t}
            </span>
          ))}
        </div>
      </div>
      <div className={`${styles.tickerWrap} ${styles.tickerWrapReverse}`}>
        <div className={styles.tickerTrackReverse}>
          {[...TICKER2, ...TICKER2].map((t, i) => (
            <span key={i} className={styles.tickerItem}>
              {t === "✦" ? <span className={styles.tickerAccent}>✦</span> : t}
            </span>
          ))}
        </div>
      </div>

      <div className="rule" />

      <section id="manifesto" className={styles.section}>
        <p className={`${styles.sectionLabel} reveal`}>Manifesto</p>
        <div className={`${styles.manifestoBlock} reveal`}>
          {MANIFESTO.map((line, i) => (
            <span key={i} className={`${styles.manifestoLine} ${i === activeManifesto ? styles.manifestoLineActive : ""}`}>{line}</span>
          ))}
          <div className={`${styles.manifestoPillars} reveal`}>
            {[
              { title: "On what we build", text: "We don't start with technology. We start with the question no one is asking yet. Then we build the answer before anyone knows they need it." },
              { title: "On how we work", text: "Quietly. Deliberately. Without announcements until the work speaks louder than words. We move in silence and release with intention." },
              { title: "On why it matters", text: "The world doesn't need more products. It needs fewer, better ones — built by people who understand the difference between making something and making something matter." },
            ].map(({ title, text }) => (
              <div key={title} className={styles.pillar}>
                <p className={styles.pillarTitle}>{title}</p>
                <p className={styles.pillarText}>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="rule" />

      <section id="about" className={styles.section}>
        <p className={`${styles.sectionLabel} reveal`}>About</p>
        <div className={styles.aboutGrid}>
          <div className={`${styles.aboutStatement} reveal`}>
            Most things are made to be seen.<br />
            We make things that are felt.<br />
            Ordinary is an option we don&apos;t consider.
          </div>
          <div className={`${styles.aboutBody} reveal`}>
            <p>Prodigium Dynamics is a studio where technology meets intention. We operate quietly, build deliberately, and release only what deserves to exist.</p>
            <p>No noise. No excess. Just work that holds its ground.</p>
          </div>
        </div>
        <div className={`${styles.studioImageWrap} reveal`}>
          <div className={styles.studioImagePlaceholder}>Prodigium — studio</div>
        </div>
        <div className={`${styles.statsRow} reveal`}>
          {[{ n: "4", label: "Active Products" }, { n: "3+", label: "In Development" }, { n: "2026", label: "Founded" }].map(({ n, label }) => (
            <div key={label}>
              <div className={styles.statNumber}>{n}</div>
              <div className={styles.statLabel}>{label}</div>
            </div>
          ))}
        </div>
      </section>

      <div className="rule" />

      <div className={styles.globeSection}>
        <h2 className={`${styles.globeTitle} reveal`}>Distance is a detail.</h2>
        <canvas ref={globeCanvasRef} width={340} height={340} className={styles.globeCanvas} />
        <p className={`${styles.globeSub} reveal`}>Prodigium operates where ideas live —<br />everywhere, always.</p>
      </div>

      <div className="rule" />

      <section id="projects" className={styles.section}>
        <p className={`${styles.sectionLabel} reveal`}>Projects</p>
        <div className={styles.projectsGrid}>
          {PROJECTS.map((p) => (
            <div key={p.id} className={`${styles.projectCard} reveal ${p.status === "soon" ? styles.projectCardDim : ""}`}>
              <div>
                {p.href ? (
                  <Link to={p.href}>
                    <div className={styles.projectImage} style={{ cursor: "pointer" }}>
                      <div className={styles.projectImagePlaceholder}>{p.image}</div>
                    </div>
                  </Link>
                ) : (
                  <div className={styles.projectImage}>
                    <div className={styles.projectImagePlaceholder}>{p.image}</div>
                  </div>
                )}
                <p className={styles.projectTag}>{p.tag}</p>
                <h3 className={styles.projectName}>{p.name}</h3>
                <p className={styles.projectDesc}>{p.desc}</p>
                {p.external && <a href={p.external} target="_blank" rel="noopener" className={styles.projectLink}>{p.external.replace("https://", "")} →</a>}
                {p.href && <Link to={p.href} className={`${styles.projectLink} ${p.external ? styles.projectLinkSub : ""}`}>{p.external ? "Read the Manifesto →" : "Explore →"}</Link>}
              </div>
              <div className={styles.projectStatus}>
                {p.status === "live" && <><span className={styles.statusDot} />Live</>}
                {p.status === "beta" && <><span className={styles.statusDot} />Beta</>}
                {p.status === "soon" && "— Soon"}
              </div>
            </div>
          ))}
          {[1, 2, 3].map((n) => (
            <div key={`secret-${n}`} className={`${styles.projectCard} ${styles.projectCardSecret} reveal`}>
              <div>
                <div className={styles.secretLock}>🔒</div>
                <p className={styles.secretLabel}>Classified — In Development</p>
              </div>
              <div className={styles.projectStatus}>—</div>
            </div>
          ))}
        </div>
      </section>

      <div className="rule" />

      <section id="contact" className={styles.section}>
        <p className={`${styles.sectionLabel} reveal`}>Contact</p>
        <div className={styles.contactBlock}>
          <div className={`${styles.contactStatement} reveal`}>
            Building something that<br />deserves to exist?<br />
            <span>Let&apos;s talk.</span>
          </div>
          <div className="reveal">
            <form className={styles.contactForm} onSubmit={handleSubmit}>
              <input type="text" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} required />
              <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              <textarea placeholder="What are you working on?" value={message} onChange={(e) => setMessage(e.target.value)} />
              <button type="submit" className={styles.btnSubmit}>{sent ? "Sent ✓" : "Send Message"}</button>
            </form>
            <p className={styles.contactAlt}>Or write directly to{" "}<a href="mailto:global@prodigiumdynamics.space">global@prodigiumdynamics.space</a></p>
          </div>
        </div>
      </section>

      <div className="rule" />
      <Footer />
    </>
  );
}
